/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { formatDateMMYYYY, getPreviousMonth, IMonthValue } from '../../util/DateUtils';
import SorterList from '../../components/SorterList';
import DateSelector from '../../components/DateSelector';
import FileUploader from '../../components/FileUploader';
import ReportFileTransfer, { IReportFile } from '../../components/ReportFileTransfer';
import * as upload from '../../api/FileTransfer';
import TransportSummaryPreview, { ITransportReportSummary } from './TransportSummaryPreview';
import { ConfirmModal, ReadyModal, WaitModal } from '../../components/ReportModals';
import SorterSummaryPreview from './SorterSummaryPreview';
import { ISortSummaryData, ISortSummaryDataCategory, ITransportSummaryCategory, sendSortsummaryReportData, sendTransportsummaryReportData } from '../../api/SortsummaryData';
import ReportTypeList from '../../components/ReportTypeList';

interface IImportSortSummaryReportProps {
  intl: any;
}

const ImportSortSummaryReport = (props: IImportSortSummaryReportProps) => {
  const [,setState] = useState({}); // just to force re-render when upload status changes
  const [date, setDate] = useState(getPreviousMonth());
  const [sorter, setSorter] = useState('');
  const [sorterName, setSorterName] = useState('');
  const [reportType, setReportType] = useState('sorter');
  const [files, setFiles] = useState([] as IReportFile[]);
  const [sendDisabled, setSendDisabled] = useState(true);
  const [hideSummary, setHideSummary] = useState(true);
  const [transportSummary, setTransportSummary] = useState({} as ITransportReportSummary);
  const [sortSummary, setSortSummary] = useState({} as ISortSummaryData);
  const [modalWaitOpen, setWaitModalOpen] = useState(false);
  const [modalConfOpen, setConfirmationModalOpen] = useState(false);
  const [modalReadyOpen, setReadyModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');

  const handleRadioChange = (e: any) => {
    setReportType(e.target.value);
    clear();
  }

  const getFileList = () => {
    const list: JSX.Element[] = [];
    let index: number = 0;
    files.forEach((file) => {
        list.push (
          <div className="row" key={++index}>
            <ReportFileTransfer {...file}/>
            <ReportTypeList sorter={sorter} multiselect={false} setType={(e) => changeReportType(file, e)}/>
          </div>
        );
      }
    )
    return list;
  }

  const changeReportType = (file: IReportFile, type: string) => {
    file.type = type;
    const index = files.findIndex((f) => f.name === file.name);
    files[index].type = type;
    files[index].progress = 0;
    if (type !== '') {
      files[index].status = 'ready';
    } else {
      files[index].status = 'added';
    }
    setFiles(files);
    checkIfSendIsDisabled();
    setState({});
  }

  const checkIfSendIsDisabled = () => {
    setFiles((fs: IReportFile[]) => {
      let result = Boolean(fs.length === 0);
      fs.forEach((f) => {
        if (f.type === '' || ['inprogress', 'error'].includes(f.status)) {
          result = true;
        }
      });
      if (reportType === 'sorter') {
        setSorter((s: string) => {
          if (!s || s === '') {
            result = true;
          }
          setSendDisabled(result);
          return s;
        });
      } else {
        setSendDisabled(result);
      }
      return fs;
    })
  }

  const clearFileStatuses = () => {
    files.forEach((f) => {
      if (f.status === 'error') {
        f.status = 'ready';
        f.progress = 0;
      }
    });
  }

  const resetSummary = () => {
    setHideSummary(true);
    setTransportSummary({} as ITransportReportSummary);
    setSortSummary({} as ISortSummaryData);
  }

  const deleteFile = (file: IReportFile) => {
    // Deletes file by name, because we don't have any other ids
    if (file.status === 'ready') {
      resetSummary();
    }
    setFiles(files => files.filter((f) => f.name !== file.name));
    checkIfSendIsDisabled();
  }

  const onFileSelect = (files: File[]) => {
    for(let i = 0; i < files.length; i++) addFile(files[i]);
    checkIfSendIsDisabled();
    setState({});
  }

  const addFile = (file: File) => {
    const newFile: IReportFile = {
      name: file.name,
      file,
      status: 'added',
      delete: () => {
        deleteFile(newFile);
      },
      progress: 0,
      type: '',
      intl: props.intl
    };
    // don't add the same file twice
    if (!files.find((f) => f.name === file.name)) {
      files.push(newFile);
      setFiles(files);
      resetSummary();
    }
  }

  const allFilesReady = () => files.every((f) => ['ready', 'finished'].includes(f.status));

  const addTransportSummaryCategory = (cat: ITransportSummaryCategory, total: ITransportSummaryCategory, fileCat: any) => {
    cat.km += parseFloat(fileCat.Km);
    cat.eur += parseFloat(fileCat.EUR);
    total.km += parseFloat(fileCat.Km);
    total.eur += parseFloat(fileCat.EUR);
    // if only one file, we'll take the percentage as it is. Otherwise it is calculated again for the whole set
    cat.eurPct = parseFloat(fileCat['%EUR']);
    total.eurPct += parseFloat(fileCat['%EUR']);
  }

  const calculateTransportSummary = () => {
    const sums: ITransportReportSummary = {
      ce: {km: 0, eur: 0, eurPct: 0},
      cold: {km: 0, eur: 0, eurPct: 0},
      crt: {km: 0, eur: 0, eurPct: 0},
      lda: {km: 0, eur: 0, eurPct: 0},
      sda: {km: 0, eur: 0, eurPct: 0},
      total: {km: 0, eur: 0, eurPct: 0}
    };
    files.forEach((file) => {
      addTransportSummaryCategory(sums.ce, sums.total, file.response.summary.CE);
      addTransportSummaryCategory(sums.cold, sums.total, file.response.summary.COLD);
      addTransportSummaryCategory(sums.crt, sums.total, file.response.summary.CRT);
      addTransportSummaryCategory(sums.lda, sums.total, file.response.summary.LDA);
      addTransportSummaryCategory(sums.sda, sums.total, file.response.summary.SDA);
    });
    if (files.length > 1) {
      // calculate percentages again
      sums.ce.eurPct = sums.ce.eur / sums.total.eur * 100;
      sums.cold.eurPct = sums.cold.eur / sums.total.eur * 100;
      sums.crt.eurPct = sums.crt.eur / sums.total.eur * 100;
      sums.sda.eurPct = sums.sda.eur / sums.total.eur * 100;
      sums.lda.eurPct = sums.lda.eur / sums.total.eur * 100;
      sums.total.eurPct = 100;
    }
    setTransportSummary(sums);
  }

  const addSortSummaryCategory = (cat: ISortSummaryDataCategory, fileCat: ISortSummaryDataCategory) => {
    cat['EUR'] += fileCat['EUR'];
    cat['KG'] += fileCat['KG'];
    cat['EUR/KG'] = cat['KG'] !== 0 ? cat['EUR']/cat['KG'] : 0;
  }

  const calculateSortSummary = () => {
    const sums = {
      WEEE1: {
        COLD: {EUR: 0, KG: 0, "EUR/KG": 0},
        LDA: {EUR: 0, KG: 0, "EUR/KG": 0},
        SCRAP: {EUR: 0, KG: 0, "EUR/KG": 0},
        SOLARPANELS: {EUR: 0, KG: 0, "EUR/KG": 0}
      },
      WEEE2: {
        SDA: {EUR: 0, KG: 0, "EUR/KG": 0},
        SCRAP: {EUR: 0, KG: 0, "EUR/KG": 0}
      },
      WEEE3: {
        ICT: {EUR: 0, KG: 0, "EUR/KG": 0},
        MONITORS: {EUR: 0, KG: 0, "EUR/KG": 0},
        SCRAP: {EUR: 0, KG: 0, "EUR/KG": 0}
      },
      WEEE4: {
        CE: {EUR: 0, KG: 0, "EUR/KG": 0},
        CRT: {EUR: 0, KG: 0, "EUR/KG": 0},
        MUSIC: {EUR: 0, KG: 0, "EUR/KG": 0},
      },
      WEEE5: {
        LAMPS: {EUR: 0, KG: 0, "EUR/KG": 0},
      },
      WEEE6: {
        EL_TOOLS: {EUR: 0, KG: 0, "EUR/KG": 0},
      },
      WEEE7: {
        TOYS: {EUR: 0, KG: 0, "EUR/KG": 0},
      },
      WEEE8: {
        HEALTH: {EUR: 0, KG: 0, "EUR/KG": 0},
      },
      WEEE9: {
        MONITORING_TOOLS: {EUR: 0, KG: 0, "EUR/KG": 0},
        FIRE_ALARMS: {EUR: 0, KG: 0, "EUR/KG": 0},
      },
      TOTAL_EUR: 0,
      TOTAL_KG: 0
    } as ISortSummaryData;
    files.forEach((file) => {
      addSortSummaryCategory(sums.WEEE1.COLD, file.response.summary.WEEE1.COLD);
      addSortSummaryCategory(sums.WEEE1.LDA, file.response.summary.WEEE1.LDA);
      addSortSummaryCategory(sums.WEEE1.SCRAP, file.response.summary.WEEE1.SCRAP);
      addSortSummaryCategory(sums.WEEE1.SOLARPANELS, file.response.summary.WEEE1.SOLARPANELS);
      addSortSummaryCategory(sums.WEEE2.SDA, file.response.summary.WEEE2.SDA);
      addSortSummaryCategory(sums.WEEE2.SCRAP, file.response.summary.WEEE2.SCRAP);
      addSortSummaryCategory(sums.WEEE3.ICT, file.response.summary.WEEE3.ICT);
      addSortSummaryCategory(sums.WEEE3.MONITORS, file.response.summary.WEEE3.MONITORS);
      addSortSummaryCategory(sums.WEEE3.SCRAP, file.response.summary.WEEE3.SCRAP);
      addSortSummaryCategory(sums.WEEE4.CE, file.response.summary.WEEE4.CE);
      addSortSummaryCategory(sums.WEEE4.CRT, file.response.summary.WEEE4.CRT);
      addSortSummaryCategory(sums.WEEE4.MUSIC, file.response.summary.WEEE4.MUSIC);
      addSortSummaryCategory(sums.WEEE5.LAMPS, file.response.summary.WEEE5.LAMPS);
      addSortSummaryCategory(sums.WEEE6.EL_TOOLS, file.response.summary.WEEE6.EL_TOOLS);
      addSortSummaryCategory(sums.WEEE7.TOYS, file.response.summary.WEEE7.TOYS);
      addSortSummaryCategory(sums.WEEE8.HEALTH, file.response.summary.WEEE8.HEALTH);
      addSortSummaryCategory(sums.WEEE9.FIRE_ALARMS, file.response.summary.WEEE9.FIRE_ALARMS);
      addSortSummaryCategory(sums.WEEE9.MONITORING_TOOLS, file.response.summary.WEEE9.MONITORING_TOOLS);
      sums.TOTAL_EUR += file.response.summary.TOTAL_EUR;
      sums.TOTAL_KG += file.response.summary.TOTAL_KG;
    });
    setSortSummary(sums);
  }

  const formatErrorMessages = (errors: upload.ITransportSummaryError[]) => {
    let errorStr = '';
    errors.forEach((error) => {
      try {
        errorStr = props.intl.formatMessage({id: `summaryreport.error.${error.errorID}`});
      } catch (error: any) {
        errorStr = error.name;
      }
      errorStr += ' ' + error.value;
    });
    return errorStr;
  }

  const sendFile = (file: IReportFile) => {
    file.status = 'inprogress';
    checkIfSendIsDisabled();
    upload.sendCostFile(
      file,
      formatDateMMYYYY(date),
      file.type,
      sorter,
      reportType === 'sorter' ? 'Sort' : 'Transport',
      (progress) => {
        file.progress = progress;
        setState({});
      },
      (response: any) => {
        if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
          file.status = 'error';
          file.errorMessage = formatErrorMessages(response.errors);
        } else {
          file.status = 'ready';
        }
        file.response = response;
        if(allFilesReady()) {
          if (reportType === 'sorter') {
            calculateSortSummary();
          } else {
            calculateTransportSummary();
          }
        }
        setHideSummary(!allFilesReady());
        checkIfSendIsDisabled();
        setState({});
      },
      (errorMsg: string, data?: any) => {
        file.status = 'error';
        file.response = data;
        try {
          file.errorMessage = props.intl.formatMessage({id: errorMsg});
        } catch (e) {
          console.error('No translation found for', errorMsg);
          file.errorMessage = errorMsg;
        }
        setState({});
      }
    );
  }

  const sendFiles = () => {
    resetSummary();
    files.forEach((f) => {
      if (!['finished', 'error'].includes(f.status)) {
        sendFile(f);
      }
    });
  }

  const clear = (clearMonth: boolean = false) => {
    if (clearMonth) {
      setDate(getPreviousMonth());
    }
    setFiles([] as IReportFile[]);
    setWaitModalOpen(false);
    setConfirmationModalOpen(false);
    setModalText('');
    setSorter('');
    setSorterName('');
    resetSummary();
    setState({});
  }

  const composeJSON = () => {
    let json = {rows: [], summary: {totalCost: 0, totalWeight: 0}};
    files.forEach((file) => {
        json.rows = json.rows.concat(file.response.rows);
        json.summary.totalCost += file.response.summary.TOTAL_EUR;
        json.summary.totalWeight += file.response.summary.TOTAL_KG;
    });
    return json;
  }

  const sendTransportsummary = () => {
    setWaitModalOpen(true);
    sendTransportsummaryReportData(formatDateMMYYYY(date), composeJSON()).then((response) => {
      if(response) {
        clear(false);
        setReadyModalOpen(true);
        setTimeout(() => setReadyModalOpen(false), 2000);
      } else {
        setModalText(props.intl.formatMessage({id:'sortsummaryreports.import.error'}));
        setConfirmationModalOpen(true);
      }
    }).finally(() => setWaitModalOpen(false));
  }

  const sendSortSummary = () => {
    setWaitModalOpen(true);
    sendSortsummaryReportData(composeJSON()).then((response) => {
      if(response) {
        clear(false);
        setReadyModalOpen(true);
        setTimeout(() => setReadyModalOpen(false), 2000);
      } else {
        setModalText(props.intl.formatMessage({id:'sortsummaryreports.import.error'}));
        setConfirmationModalOpen(true);
      }
    }).finally(() => setWaitModalOpen(false));
  }

  return (
    <div>
      <WaitModal
        messageId={modalWaitOpen ? "sortsummaryreports.import.saving" : "sortsummaryreports.import.getting"}
        modalOpen={modalWaitOpen}
        setModalOpen={(open: boolean) => setWaitModalOpen(open)}
      />
      <ReadyModal
        messageId="sortsummaryreports.import.ready"
        modalOpen={modalReadyOpen}
        setModalOpen={(open: boolean) => setReadyModalOpen(open)}
      />
      <ConfirmModal
        modalOpen={modalConfOpen}
        labelId={'handlerreports.import.rejectreport'}
        messageId={"handlerreports.import.rejectreporttext"}
        modalText={modalText}
        submitId={'handlerreports.import.edit'} dismissId={'handlerreports.import.reject'}
        onDismiss={() => { setConfirmationModalOpen(false); clear(); } }
        onSubmit={() => { setConfirmationModalOpen(false); } } />
      <h1>
        <FormattedMessage id="sortsummaryreports.costincome.header"/>
        <br/>
        <FormattedMessage id="sortsummaryreports.import.header"/>
      </h1>

      <div className="search-wrap">
        <Row>

          <Col md="12" className="radio-set">
            <label><FormattedMessage id="sortsummaryreports.import.selectcostincome"/></label>
            <div className="radio-value">
              <Form.Group>
                <input
                  type="radio"
                  value="sorter"
                  checked={reportType === 'sorter'}
                  onChange={handleRadioChange}
                />
                <label htmlFor="sorter-select" className={reportType === 'sorter' ? 'label' : 'disabled label'}><FormattedMessage id="sortsummaryreports.header"/></label>
              </Form.Group>
              <Form.Group>
                <select className="sorter-select form-control"
                        id="sorter-select"
                        value={sorter}
                        onChange={(e) => {
                          setSorter(e.target.value);
                          setSorterName(e.target.options[e.target.selectedIndex].text);
                          clearFileStatuses();
                          checkIfSendIsDisabled();
                          setState({});
                        }}
                        disabled={reportType !== 'sorter'}>
                  { SorterList(props.intl) }
                </select>
              </Form.Group>
            </div>

            <div className="radio-value">
              <Form.Group>
              <input
                type="radio"
                value="expenses"
                checked={reportType === 'expenses'}
                onChange={handleRadioChange}
                id="expenses"
              />
              <label htmlFor="expenses" className={reportType === 'expenses' ? 'label' : 'disabled label'}>
                <FormattedMessage id="sortsummaryreports.import.expenses"/>
              </label>
              </Form.Group>
            </div>
          </Col>
          <Col md="12">
            <Form.Group>
              <label><FormattedMessage id="handlerreports.import.reportmonth"/></label>
              <DateSelector startPeriod={date} 
                            onChange={(start: IMonthValue) => { setDate(start); clearFileStatuses(); checkIfSendIsDisabled(); } } 
                            dateFormat={'yyyy-MM'} />
            </Form.Group>
          </Col>
          <Col md="12">
            <Form.Group>
              <label htmlFor="file-upload"><FormattedMessage id="handlerreports.import.importreport"/></label>
              <FileUploader id="file-upload" onFileSelect={onFileSelect} label={props.intl.formatMessage({id: 'handlerreports.import.uploadfile'})}/>
              { getFileList() }
            </Form.Group>
          </Col>

          <Col md="12" className="form-buttons d-flex flex-row">
            <button className="clear-button btn btn-secondary ml-auto"
                    disabled={(reportType==='sorter' && files.length===0 && !sorter) ||
                              (reportType==='expenses' && files.length===0)}
                    onClick={() => { clear(true); }}>
              <FormattedMessage id="sortsummaryreports.settings.reset"/>
            </button>
            <button className={"send-button btn btn-primary"}
                    disabled={sendDisabled}
                    onClick={() => { sendFiles(); }}>
              <FormattedMessage id="handlerreports.import.send"/>
            </button>
          </Col>
        </Row>

      </div>
      <div className="search-results">
        {!hideSummary && reportType === 'expenses' &&
          <TransportSummaryPreview
            summary={transportSummary}
            date={date}
            onSend={() => sendTransportsummary()}
            onCancel={() => resetSummary()}
          />
        }
        {!hideSummary && reportType === 'sorter' &&
          <SorterSummaryPreview
            summary={sortSummary}
            date={date}
            sorterName={sorterName}
            onSend={() => sendSortSummary()}
            onCancel={() => resetSummary()}
          />
        }
      </div>

    </div>
  )
}

export default injectIntl(ImportSortSummaryReport);
